import {
  Alert,
  Box,
  Button,
  Collapsible,
  HStack,
  Image,
  List,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import React from 'react';
import { LuChevronDown } from 'react-icons/lu';
import { TrackingType } from '@/types';
import { toDisplay } from '../utils';

interface PreviewPanelProps {
  preview: TrackingType.PreviewResponse | null;
  loading: boolean;
  unit: 'mm' | 'in';
  labelCount: number;
  mode: 'labels' | 'reports';
  specErrorMessages: string[];
}

export const PreviewPanel: React.FC<PreviewPanelProps> = ({
  preview,
  loading,
  unit,
  labelCount,
  mode,
  specErrorMessages,
}) => {
  const [zoom, setZoom] = React.useState(1);
  if (mode === 'reports') {
    return (
      <Stack gap={2}>
        <Text textStyle="fieldLabel.bold">Report output</Text>
        <Text textStyle="body.small" color="fg.muted">
          Reports are generated from the latest authorized information when you
          prepare them. A printed report is a dated snapshot; its QR code keeps
          opening the live report.
        </Text>
      </Stack>
    );
  }
  const layout = preview?.layout;
  const errors = [...(layout?.errors ?? []), ...(preview?.media_issues ?? [])];
  const warnings = layout?.warnings ?? [];
  const dims = layout
    ? `${toDisplay(layout.width_mm, unit)} × ${toDisplay(layout.height_mm, unit)} ${unit}`
    : null;
  return (
    <Stack gap={3}>
      <HStack justify="space-between">
        <Text textStyle="fieldLabel.bold">Label preview</Text>
        <HStack gap={1}>
          {loading && <Spinner size="sm" aria-label="Updating preview" />}
          <Button size="xs" variant="ghost" onClick={() => setZoom(1)}>
            Fit
          </Button>
          <Button
            size="xs"
            variant="ghost"
            onClick={() => setZoom((z) => Math.min(2.5, Number((z + 0.25).toFixed(2))))}
          >
            Larger
          </Button>
        </HStack>
      </HStack>
      <Box
        bg="bg.muted"
        borderRadius="md"
        p={4}
        minH="160px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="auto"
        aria-live="polite"
      >
        {preview?.image ? (
          <Image
            src={preview.image}
            alt={`Label preview, ${dims}. Screen preview is not actual size.`}
            maxW={zoom === 1 ? '100%' : undefined}
            w={zoom === 1 ? undefined : `${Math.round(zoom * 100)}%`}
            maxH={zoom === 1 ? '320px' : undefined}
            bg="white"
            boxShadow="sm"
          />
        ) : (
          <Text textStyle="body.small" color="fg.muted">
            {errors.length || specErrorMessages.length
              ? 'Fix the settings to see a preview.'
              : 'Preview will appear here.'}
          </Text>
        )}
      </Box>
      {preview?.sheet_image && (
        <Box>
          <Text fontSize="xs" color="fg.muted" mb={1}>
            First sheet
          </Text>
          <Image
            src={preview.sheet_image}
            alt="Sheet layout preview"
            maxW="100%"
            maxH="300px"
            bg="white"
            boxShadow="sm"
          />
        </Box>
      )}
      <Text fontSize="sm" color="fg.muted">
        {dims ?? 'Size not ready'}
        {' · '}
        {labelCount} label{labelCount === 1 ? '' : 's'}
        {preview?.pages !== undefined
          ? ` · ${preview.pages} page${preview.pages === 1 ? '' : 's'}`
          : ''}
        {loading ? ' · Updating preview' : ''}
        {preview?.sample ? ' · Sample code, not printable' : ''}
      </Text>
      {layout?.symbols.length ? (
        <Collapsible.Root>
          <Collapsible.Trigger asChild>
            <Button size="xs" variant="ghost" px={0}>
              <LuChevronDown /> Print quality details
            </Button>
          </Collapsible.Trigger>
          <Collapsible.Content>
            <Stack gap={1} pt={1}>
              {layout.symbols.map((s) => (
                <Text key={s.format} fontSize="xs" color="fg.muted">
                  {s.format.toUpperCase()} {s.width_mm.toFixed(1)} ×{' '}
                  {s.height_mm.toFixed(1)} mm · module {s.module_mm.toFixed(3)} mm
                  {s.dots_per_module ? ` (${s.dots_per_module} dots)` : ''}
                </Text>
              ))}
            </Stack>
          </Collapsible.Content>
        </Collapsible.Root>
      ) : null}
      {specErrorMessages.length > 0 && (
        <Alert.Root status="error">
          <Alert.Indicator />
          <Box>
            <Alert.Title>Check these settings</Alert.Title>
            <List.Root fontSize="sm">
              {specErrorMessages.map((m) => (
                <List.Item key={m}>{m}</List.Item>
              ))}
            </List.Root>
          </Box>
        </Alert.Root>
      )}
      {errors.map((e, i) => (
        <Alert.Root key={`${e.code}-${i}`} status="error">
          <Alert.Indicator />
          <Box>
            <Alert.Title>{e.message}</Alert.Title>
            {e.suggestions && e.suggestions.length > 0 && (
              <Alert.Description>
                <List.Root fontSize="sm" mt={1}>
                  {e.suggestions.map((s) => (
                    <List.Item key={s}>{s}</List.Item>
                  ))}
                </List.Root>
              </Alert.Description>
            )}
          </Box>
        </Alert.Root>
      ))}
      {warnings.length > 0 && (
        <Alert.Root status="warning">
          <Alert.Indicator />
          <List.Root fontSize="sm">
            {warnings.map((w, i) => (
              <List.Item key={i}>{w.message}</List.Item>
            ))}
          </List.Root>
        </Alert.Root>
      )}
      <Text fontSize="xs" color="fg.muted">
        The preview is rendered from the same file that prints. Print at “Actual
        size” / 100% with scaling off.
      </Text>
    </Stack>
  );
};
